import styled from '@emotion/styled';
import {
  Hearing,
  InfoOutlined,
  Mail,
  OpenInFull,
  Share,
} from '@mui/icons-material';
import { Button, IconButton, Popover, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import config from '../../../config';
import paths from '../../../config/paths';
import {
  BackButton,
  Container,
  LinkSettingsDialog,
  Loading,
  ReadSpeakerButton,
  RouteBar,
  SearchBar,
  SimpleListItem,
  SMButton,
  TabLists,
  TitleBar,
  TitledList,
} from '../../components';
import { fetchServiceUnits } from '../../redux/actions/services';
import {
  activateSetting,
  resetSenseSettings,
  setMapType,
} from '../../redux/actions/settings';
import { selectMapRef, selectNavigator } from '../../redux/selectors/general';
import {
  getSelectedUnit,
  selectEvents,
  selectHearingMapsData,
  selectReservations,
  selectSelectedUnitAccessibilitySentencesData,
  selectSelectedUnitIsFetching,
} from '../../redux/selectors/selectedUnit';
import { selectUserPosition } from '../../redux/selectors/user';
import { parseSearchParams } from '../../utils';
import useMobileStatus from '../../utils/isMobile';
import { mapHasMapPane } from '../../utils/mapUtility';
import UnitHelper from '../../utils/unitHelper';
import useIsClient from '../../utils/useIsClient';
import useLocaleText from '../../utils/useLocaleText';
import AccessibilityInfo from './components/AccessibilityInfo';
import ContactInfo from './components/ContactInfo';
import Description from './components/Description';
import ElectronicServices from './components/ElectronicServices';
import Highlights from './components/Highlights';
import PriceInfo from './components/PriceInfo';
import ReadFeedbackLink from './components/ReadFeedbackLink/ReadFeedbackLink';
import SocialMediaLinks from './components/SocialMediaLinks';
import UnitDataList from './components/UnitDataList';
import UnitLinks from './components/UnitLinks';
import UnitsServicesList from './components/UnitsServicesList';
import { parseUnitViewUrlParams } from './utils/unitViewUrlParamAndSettingsHandler';

// Lazy load MapView to avoid server-side loading issues with react-leaflet
const MapView = React.lazy(() => import('../MapView'));

function splitLineBreakGetFirstItem(extraElement) {
  return extraElement?.split(/\r?\n/)?.[0];
}

function hasPhotoMetadata(metadata) {
  return Boolean(metadata?.trim());
}

function UnitView(props) {
  const {
    embed = false,
    fetchSelectedUnit,
    fetchUnitEvents,
    fetchReservations,
    fetchAccessibilitySentences,
    fetchHearingMaps,
  } = props;
  const intl = useIntl();
  const navigator = useSelector(selectNavigator);
  const userLocation = useSelector(selectUserPosition);
  const hearingMaps = useSelector(selectHearingMapsData);
  const reservationsData = useSelector(selectReservations);
  const eventsData = useSelector(selectEvents);
  const accessibilitySentences = useSelector(
    selectSelectedUnitAccessibilitySentencesData
  );
  const unitFetching = useSelector(selectSelectedUnitIsFetching);
  const stateUnit = useSelector(getSelectedUnit);
  const map = useSelector(selectMapRef);
  const location = useLocation();
  const { unit: unitParam } = useParams();

  const checkCorrectUnit = (unit) =>
    unit && unit.id === Number.parseInt(unitParam, 10);

  const [unit, setUnit] = useState(
    checkCorrectUnit(stateUnit) ? stateUnit : null
  );
  const viewPosition = useRef(null);
  const isFirstRender = useRef(true);

  const isMobile = useMobileStatus();
  const isClient = useIsClient();
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [photoInfoAnchorEl, setPhotoInfoAnchorEl] = useState(null);
  const getLocaleText = useLocaleText();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessiblityTabRef = useRef();

  const getImageAlt = () =>
    `${intl.formatMessage({ id: 'unit.picture' })}${getLocaleText(unit.name)}`;

  function resetUrlSearchParams() {
    const search = new URLSearchParams(location.search);
    const hasParams =
      search.has('mobility') || search.has('senses') || search.has('map');

    if (!hasParams) {
      return; // No need to navigate if no parameters to remove
    }

    search.delete('mobility');
    search.delete('senses');
    search.delete('map');

    navigate({ search: search.toString() }, { replace: true });
  }

  useEffect(() => {
    const actions = parseUnitViewUrlParams(location.search);
    actions
      .filter(({ setting }) => setting === 'mobility')
      .forEach(({ setting, value }) => {
        dispatch(activateSetting(setting, value));
      });
    const senses = actions.filter(({ setting }) => setting === 'senses');
    // if a { setting: 'senses', value: null} is returned then this will reset
    if (senses.length) {
      dispatch(resetSenseSettings());
    }
    senses
      .filter(({ value }) => !!value)
      .forEach(({ value }) => {
        dispatch(activateSetting(value));
      });
    actions
      .filter(({ setting }) => setting === 'mapType')
      .forEach(({ value }) => {
        dispatch(setMapType(value));
      });
    resetUrlSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePTVAccessibilitySentences = () => {
    if (unit) {
      unit.identifiers.forEach((element) => {
        if (element.namespace === 'ptv') {
          const ptvId = element.value;
          fetchAccessibilitySentences(ptvId);
        }
      });
    }
  };

  const unitHasHearingMaps = (unit) =>
    unit?.keywords?.fi?.some(
      (keyword) => keyword.toLowerCase() === 'kuuluvuuskartta'
    );

  const intializeUnitData = () => {
    const unitId = unitParam;

    // If no selected unit data, or selected unit data is old, fetch new data
    if (!stateUnit || !checkCorrectUnit(stateUnit) || !stateUnit.complete) {
      fetchSelectedUnit(unitId, (unit) => {
        setUnit(unit);
        if (unitHasHearingMaps(unit)) {
          fetchHearingMaps(unitId);
        }
      });
      fetchAccessibilitySentences(unitId);
      fetchReservations(unitId);
      fetchUnitEvents(unitId);
    } else {
      // If selected unit data is correct, but some info is missing, fetch them
      if (!hearingMaps && unitHasHearingMaps(stateUnit)) {
        fetchHearingMaps(unitId);
      }
      if (!accessibilitySentences) {
        fetchAccessibilitySentences(unitId);
      }
      if (!reservationsData.data) {
        fetchReservations(unitId);
      }
      if (!eventsData.data) {
        fetchUnitEvents(unitId);
      }
    }
  };

  const handleServiceFetch = () => {
    // Fetch additional data based on URL parameters
    const searchParams = parseSearchParams(location.search);
    if (searchParams.services) {
      dispatch(fetchServiceUnits(searchParams.services));
    }
  };

  const handleFeedbackClick = () => {
    const URLs = config.additionalFeedbackURLs;
    if (unit.municipality === 'espoo') {
      window.open(getLocaleText(URLs.espoo));
    } else if (unit.municipality === 'vantaa') {
      window.open(getLocaleText(URLs.vantaa));
    } else if (unit.municipality === 'kauniainen') {
      window.open(getLocaleText(URLs.kauniainen));
    } else if (unit.municipality === 'kirkkonummi') {
      window.open(getLocaleText(URLs.kirkkonummi));
    } else {
      navigator.push('unit', { id: unit.id, type: 'feedback' });
    }
  };

  const saveMapPosition = () => {
    // Remember user's map postition to return to on unmount
    if (map && mapHasMapPane(map)) {
      viewPosition.current = {
        center: map.getCenter(),
        zoom: map.getZoom(),
      };
    }
  };

  const feedbackButton = () => (
    <SMButton
      messageID="home.send.feedback"
      icon={<Mail />}
      onClick={() => handleFeedbackClick()}
      margin
      role="link"
      id="UnitFeedbackButton"
    />
  );

  useEffect(() => {
    // On mount
    intializeUnitData();
    handleServiceFetch();
    saveMapPosition();
    return () => {
      // On unmount
      // Return map to previous position if returning to search page or service page
      const isSearchPage = paths.search.regex.test(window.location.href);
      const isServicePage = paths.service.regex.test(window.location.href);
      if (map && (isSearchPage || isServicePage)) {
        map.setView(viewPosition?.current?.center, viewPosition?.current?.zoom);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If unit changes without the component unmounting, update data.
    // Skip the initial mount, which is already handled by the on-mount effect
    // above. Without this guard both effects run on mount and every request
    // (unit, events, accessibility, reservations) is sent twice.
    setPhotoInfoAnchorEl(null);
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (unit) {
      intializeUnitData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitParam]);

  useEffect(() => {
    if (config.usePtvAccessibilityApi) {
      initializePTVAccessibilitySentences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  if (embed) {
    return null;
  }

  // Renders hidden title text for readpseaker
  const renderTitleForRS = () => {
    const title = unit && unit.name ? getLocaleText(unit.name) : '';
    return (
      <Typography style={visuallyHidden} aria-hidden>
        {title}
      </Typography>
    );
  };

  const getPictureUrlAndCaption = () => {
    const { extra } = unit;
    const pictureUrlFromExtra = splitLineBreakGetFirstItem(
      extra?.['kaupunkialusta.photoUrl']
    );
    const pictureUrl = unit.picture_url || pictureUrlFromExtra;

    if (!pictureUrl) {
      return {};
    }

    const pictureCaptionFromExtra = {
      fi: splitLineBreakGetFirstItem(extra?.['kaupunkialusta.photoFi']),
      en: splitLineBreakGetFirstItem(extra?.['kaupunkialusta.photoEn']),
      sv: splitLineBreakGetFirstItem(extra?.['kaupunkialusta.photoSv']),
    };

    const photoSource = splitLineBreakGetFirstItem(
      extra?.['kaupunkialusta.photoSource']
    );
    const photoPermission = splitLineBreakGetFirstItem(
      extra?.['kaupunkialusta.photoPermission']
    );

    const getPhotoPermission = (permission) => {
      if (permission !== 'Location only') {
        return permission;
      }
      const locale = intl.locale?.split('-')[0];
      const messageId =
        {
          fi: 'unit.picture.locationOnly.fi',
          sv: 'unit.picture.locationOnly.sv',
          en: 'unit.picture.locationOnly.en',
        }[locale] || 'unit.picture.locationOnly.en';
      return intl.formatMessage({ id: messageId });
    };

    const hasPictureCaptionFromExtra = Object.values(
      pictureCaptionFromExtra
    ).some(Boolean);

    return {
      pictureUrl,
      pictureCaption:
        unit.picture_caption ||
        (hasPictureCaptionFromExtra ? pictureCaptionFromExtra : null),
      photoSource,
      photoPermission: getPhotoPermission(photoPermission),
    };
  };

  const renderPicture = () => {
    const { pictureUrl, pictureCaption, photoSource, photoPermission } =
      getPictureUrlAndCaption();
    if (!pictureUrl) {
      return null;
    }
    const styledImage = <StyledImage alt={getImageAlt()} src={pictureUrl} />;
    const hasPhotoInfo =
      hasPhotoMetadata(photoSource) || hasPhotoMetadata(photoPermission);
    const photoInfoPopupId = 'unit-picture-info-popup';
    const photoInfoTitleId = 'unit-picture-info-title';
    const photoInfoDescriptionId = 'unit-picture-info-description';
    return (
      <StyledImageContainer>
        <StyledWatermarkedImageContainer>
          {styledImage}
        </StyledWatermarkedImageContainer>
        {(pictureCaption || hasPhotoInfo) && (
          <StyledImageCaption>
            {pictureCaption && (
              <StyledPhotoCaption variant="body2">
                {typeof pictureCaption === 'string'
                  ? pictureCaption
                  : getLocaleText(pictureCaption)}
              </StyledPhotoCaption>
            )}
            {hasPhotoInfo && (
              <>
                <StyledPhotoInfoButton
                  aria-label={intl.formatMessage({ id: 'unit.picture.info' })}
                  aria-haspopup="dialog"
                  aria-controls={photoInfoPopupId}
                  aria-expanded={Boolean(photoInfoAnchorEl)}
                  onClick={(event) => setPhotoInfoAnchorEl(event.currentTarget)}
                  size="small"
                >
                  <InfoOutlined fontSize="small" />
                </StyledPhotoInfoButton>
                <Popover
                  anchorEl={photoInfoAnchorEl}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  onClose={() => setPhotoInfoAnchorEl(null)}
                  open={Boolean(photoInfoAnchorEl)}
                  slotProps={{
                    paper: {
                      'aria-describedby': photoInfoDescriptionId,
                      'aria-labelledby': photoInfoTitleId,
                      id: photoInfoPopupId,
                      role: 'dialog',
                      sx: {
                        width: 'min(400px, calc(100vw - 40px))',
                        maxWidth: 'calc(100vw - 40px)',
                        overflow: 'visible',
                        marginBottom: 1,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: -8,
                          right: 8,
                          width: 16,
                          height: 16,
                          backgroundColor: '#fff',
                          clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                        },
                      },
                    },
                  }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                  <StyledPhotoInfoPopup id={photoInfoDescriptionId}>
                    <StyledPhotoInfoTitle
                      id={photoInfoTitleId}
                      variant="subtitle2"
                    >
                      <FormattedMessage id="unit.picture.info" />
                    </StyledPhotoInfoTitle>
                    {hasPhotoMetadata(photoSource) && (
                      <StyledPhotoInfoItem>
                        <StyledPhotoInfoLabel variant="body2">
                          <FormattedMessage id="unit.picture.source" />
                        </StyledPhotoInfoLabel>
                        <StyledPhotoInfoValue variant="body2">
                          {photoSource}
                        </StyledPhotoInfoValue>
                      </StyledPhotoInfoItem>
                    )}
                    {hasPhotoMetadata(photoPermission) && (
                      <StyledPhotoInfoItem>
                        <StyledPhotoInfoLabel variant="body2">
                          <FormattedMessage id="unit.picture.permission" />
                        </StyledPhotoInfoLabel>
                        <StyledPhotoInfoValue variant="body2">
                          {photoPermission}
                        </StyledPhotoInfoValue>
                      </StyledPhotoInfoItem>
                    )}
                  </StyledPhotoInfoPopup>
                </Popover>
              </>
            )}
          </StyledImageCaption>
        )}
      </StyledImageContainer>
    );
  };

  const renderDetailTab = () => {
    if (!unit || !unit.complete) {
      return null;
    }
    const contractText = UnitHelper.getContractText(unit, intl, getLocaleText);

    let detailReadSpeakerButton = null;

    if (config.showReadSpeakerButton) {
      detailReadSpeakerButton = (
        <StyledReadSpeakerButton readID="rscontent-unitdetail" />
      );
    }

    return (
      <StyledContentContainer>
        {detailReadSpeakerButton}
        <div id="rscontent-unitdetail">
          {renderTitleForRS()}
          {/* Contract type */}
          <Container margin text>
            <Typography variant="body2">
              {contractText && `${contractText}. `}
              {unit.data_source &&
                unit.contract_type.id !== 'PRIVATE_SERVICE' && (
                  <FormattedMessage
                    id="unit.data_source"
                    defaultMessage="Source: {data_source}"
                    values={{ data_source: unit.data_source }}
                  />
                )}
            </Typography>
          </Container>

          {/* View Components */}
          <ContactInfo
            unit={unit}
            userLocation={userLocation}
            accessiblityTabRef={accessiblityTabRef}
          />
          <SocialMediaLinks unit={unit} />
          <UnitDataList listLength={3} data={eventsData} type="events" />
          <Highlights unit={unit} />
          <Description unit={unit} />
          <PriceInfo unit={unit} />
          <UnitLinks unit={unit} />
          <ElectronicServices unit={unit} />
          {!isMobile && feedbackButton()}
          {!isMobile && (
            <ReadFeedbackLink unit={UnitHelper.setDefaults(unit)} />
          )}
          {isMobile && renderPicture()}
        </div>
      </StyledContentContainer>
    );
  };

  const renderAccessibilityTab = () => {
    let accessibilityReadSpeakerButton = null;

    if (config.showReadSpeakerButton) {
      accessibilityReadSpeakerButton = (
        <StyledReadSpeakerButton
          readID="rscontent"
          encodedURL={encodeURI(
            `palvelukartta.test.hel.ninja${location.pathname}${location.search}`
          )}
        />
      );
    }

    return (
      <StyledContentContainer>
        {accessibilityReadSpeakerButton}
        <StyledTabAdjuster id="rscontent">
          {renderTitleForRS()}
          {hearingMaps?.id === unit.id.toString(10) && (
            <TitledList
              titleComponent="h4"
              title={intl.formatMessage({
                id: 'unit.accessibility.hearingMaps',
              })}
            >
              {hearingMaps.data.map((item) => (
                <SimpleListItem
                  role="link"
                  link
                  divider
                  icon={<Hearing />}
                  key={item.name}
                  text={`${item.name} ${intl.formatMessage({ id: 'opens.new.tab' })}`}
                  handleItemClick={() => window.open(item.url)}
                />
              ))}
            </TitledList>
          )}
          <AccessibilityInfo titleAlways headingLevel={4} />
        </StyledTabAdjuster>
      </StyledContentContainer>
    );
  };

  const renderServiceTab = () => {
    if (!unit || !unit.complete) {
      return null;
    }

    return (
      <StyledContentContainer>
        <UnitsServicesList listLength={5} unit={unit} />
        <UnitDataList
          listLength={5}
          data={reservationsData}
          type="reservations"
        />
      </StyledContentContainer>
    );
  };

  const renderHead = () => {
    if (!unit || !unit.complete) {
      return null;
    }
    const title = unit && unit.name ? getLocaleText(unit.name) : '';
    const imageAlt = getImageAlt();
    const description = unit.description
      ? getLocaleText(unit.description)
      : null;

    const { pictureUrl } = getPictureUrlAndCaption();
    return (
      <Helmet>
        <meta property="og:title" content={title} />
        {description && (
          <meta property="og:description" content={description} />
        )}
        {pictureUrl && <meta property="og:image" content={pictureUrl} />}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:image:alt" content={imageAlt} />
      </Helmet>
    );
  };

  const renderUnitLocation = () => (
    <StyledUnitLocationContainer>
      <StyledMapButton
        role="link"
        color="primary"
        aria-label={intl.formatMessage({ id: 'map.button.expand.aria' })}
        icon={<StyledMapIcon />}
        onClick={(e) => {
          e.preventDefault();
          if (navigator) {
            navigator.openMap();
          }
        }}
      >
        <Typography sx={{ fontSize: '0.875rem', fontWeight: '500' }}>
          <FormattedMessage id="map.button.expand" />
        </Typography>
      </StyledMapButton>
      <StyledMapContainer>
        {isClient ? (
          <Suspense fallback={<Loading />}>
            <MapView disableInteraction />
          </Suspense>
        ) : (
          <Loading />
        )}
      </StyledMapContainer>
    </StyledUnitLocationContainer>
  );

  const render = () => {
    const title = unit && unit.name ? getLocaleText(unit.name) : '';
    const onLinkOpenClick = () => {
      setOpenLinkDialog(true);
    };
    const elem = (
      <StyledLinkButton onClick={onLinkOpenClick}>
        <Typography fontSize="0.773rem" color="inherit" variant="body2">
          <FormattedMessage id="general.share.link" />
        </Typography>
        <StyledLinkButtonIcon />
      </StyledLinkButton>
    );

    const backButtonText = intl.formatMessage({ id: 'general.backTo' });
    const TopArea = (
      <>
        <BackButton
          text={backButtonText}
          ariaLabel={backButtonText}
          variant="topBackButton"
        />
        {!isMobile && <SearchBar hideBackButton />}
        <TitleBar sticky title={title} titleComponent="h3" shareLink={elem} />
        {unit?.location?.coordinates && (
          <RouteBar unit={unit} userLocation={userLocation} />
        )}
      </>
    );

    if (unitFetching) {
      return (
        <StyledRootContainer>
          <div className="Content">
            {TopArea}
            <p>
              <FormattedMessage id="general.loading" />
            </p>
          </div>
        </StyledRootContainer>
      );
    }

    if (unit && unit.complete) {
      const tabs = [
        {
          id: 'basicInfo',
          ariaLabel: intl.formatMessage({ id: 'unit.basicInfo' }),
          component: renderDetailTab(),
          data: null,
          itemsPerPage: null,
          title: intl.formatMessage({ id: 'unit.basicInfo' }),
        },
        {
          id: 'accessibilityDetails',
          ariaLabel: intl.formatMessage({ id: 'accessibility' }),
          component: renderAccessibilityTab(),
          data: null,
          itemsPerPage: null,
          title: intl.formatMessage({ id: 'accessibility' }),
          ref: accessiblityTabRef,
        },
        {
          id: 'services',
          ariaLabel: intl.formatMessage({ id: 'service.tab' }),
          component: renderServiceTab(),
          data: null,
          itemsPerPage: null,
          title: intl.formatMessage({ id: 'service.tab' }),
        },
      ];
      return (
        <div>
          {openLinkDialog && <LinkSettingsDialog setOpen={setOpenLinkDialog} />}
          {renderHead()}
          <TabLists
            data={tabs}
            headerComponents={
              <>
                {TopArea}
                {/* Unit image */}
                {isMobile ? renderUnitLocation() : renderPicture()}
              </>
            }
          />
        </div>
      );
    }

    return (
      <StyledRootContainer>
        <div className="Content">
          {TopArea}
          <Typography color="primary" variant="body1">
            <FormattedMessage id="unit.details.notFound" />
          </Typography>
        </div>
      </StyledRootContainer>
    );
  };

  return render();
}

export default UnitView;

const StyledMapIcon = styled(OpenInFull)(({ theme }) => ({
  order: 2,
  marginRight: '-4px',
  paddingLeft: theme.spacing(1),
  fontSize: '18px',
}));

const StyledRootContainer = styled.div(() => ({
  height: '100%',
  display: 'flex',
  flexFlow: 'column',
  overflowY: 'auto',
}));

const StyledContentContainer = styled.div(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const StyledTabAdjuster = styled.div(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const StyledReadSpeakerButton = styled(ReadSpeakerButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginLeft: theme.spacing(2),
}));

const preventOpenImageInNewTabClass = () => ({
  '&:after': {
    content: '" "',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 99,
  },
});

const StyledImageContainer = styled.div(() => {
  const styles = {
    width: '100%',
    height: 200,
    position: 'relative',
  };
  return Object.assign(styles, preventOpenImageInNewTabClass());
});

const StyledImage = styled.img(() => ({
  objectFit: 'cover',
  height: '100%',
  width: '100%',
}));

const StyledWatermarkedImageContainer = styled.div(() => ({
  position: 'relative',
  height: '100%',
}));

const StyledPhotoInfoButton = styled(IconButton)(() => ({
  marginLeft: 'auto',
  marginRight: '8px',
  flexShrink: 0,
  color: '#000',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
  },
}));

const StyledPhotoInfoPopup = styled.div(({ theme }) => ({
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(1.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const StyledPhotoInfoTitle = styled(Typography)(() => ({
  fontWeight: 700,
}));

const StyledPhotoInfoItem = styled.div(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const StyledPhotoInfoLabel = styled(Typography)(() => ({
  fontWeight: 700,
}));

const StyledPhotoInfoValue = styled(Typography)(() => ({
  whiteSpace: 'pre-line',
}));

const StyledImageCaption = styled.div(() => ({
  width: '100%',
  minHeight: 31,
  zIndex: 100,
  fontSize: '0.75rem',
  lineHeight: '15px',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  bottom: 0,
  left: 0,
  backgroundColor: '#F0F0F0',
  boxSizing: 'border-box',
  textAlign: 'left',
}));

const StyledPhotoCaption = styled(Typography)(({ theme }) => ({
  color: '#000',
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
}));

const StyledUnitLocationContainer = styled.div(() => ({
  height: 225,
  position: 'relative',
}));

const StyledMapButton = styled(SMButton)(({ theme }) => ({
  position: 'absolute',
  right: 16,
  margin: 0,
  top: 16,
  zIndex: 51,
  minHeight: 36,
  borderRadius: '8px',
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.75),
}));

const StyledMapContainer = styled.div(() => ({
  height: '100%',
  pointerEvents: 'none',
}));

const StyledLinkButton = styled(Button)(() => ({
  color: 'white',
  textTransform: 'none',
  marginLeft: 'auto',
}));

const StyledLinkButtonIcon = styled(Share)(({ theme }) => ({
  fontSize: 24,
  marginLeft: theme.spacing(1.5),
}));

// Typechecking
UnitView.propTypes = {
  embed: PropTypes.bool,
  fetchAccessibilitySentences: PropTypes.func.isRequired,
  fetchReservations: PropTypes.func.isRequired,
  fetchSelectedUnit: PropTypes.func.isRequired,
  fetchUnitEvents: PropTypes.func.isRequired,
  fetchHearingMaps: PropTypes.func.isRequired,
};
